import { fromUnixTime } from 'date-fns';
import type { ReactNode } from 'react';
import { useMemo } from 'react';

import type { ApiISODateTimeString } from 'api/types/ApiTypedDate';
import { BodyMicro } from 'components/Text/BodyMicro';
import { useUser } from 'contexts/UserEnvironmentContext';
import { useI18n } from 'i18n/hooks/useI18n';
import type { SearchAlgoliaEventHit } from 'modules/search/algolia/types/SearchAlgoliaEventHit';
import { SearchHitImage } from 'modules/search/components/Hit/Image/SearchHitImage';
import { SearchHitFloatingSaveButton } from 'modules/search/components/Hit/SaveButton/SearchHitFloatingSaveButton';
import {
  SearchHitCard,
  SearchHitHolder,
  SearchHitHoverEffect,
  SearchHitImageHolder,
} from 'modules/search/components/Hit/SearchHit.styled';
import { SearchHitInteractiveContentWrapper } from 'modules/search/components/Hit/SearchHitInteractiveContentWrapper';
import type { SearchContextHeaderLocationState } from 'modules/search/containers/SearchContextHeader/SearchContextHeaderLocationState';
import type { SearchSavedListingsStore } from 'modules/search/zustand-stores/searchSavedListingsStore';
import { getWorkLocationString } from 'utils/address/getWorkLocationString';
import { distanceInWordsToNow } from 'utils/date';

import { SearchEventHitInfo } from './SearchEventHitInfo';

type Props = {
  hit: SearchAlgoliaEventHit;
  href: string;
  trackClick: (() => void) | null;
  showLabel: boolean;
  searchSavedListingsStore: SearchSavedListingsStore | null;
  hideOwnerBanner: boolean;
  highlighted: boolean;
  application: {
    status: 'ACTIVE' | 'INACTIVE';
    created: ApiISODateTimeString;
  } | null;
};

export function SearchEventHit({
  hit,
  href,
  trackClick,
  showLabel,
  searchSavedListingsStore,
  hideOwnerBanner,
  highlighted,
  application,
}: Props) {
  const { i18n } = useI18n();

  const { user } = useUser();
  const {
    objectID,
    name,
    city,
    published,
    locationType,
    remoteZone,
    image,
    startDate,
    startTime,
    endDate,
    endTime,
    endsLocal,
    startsLocal,
    timezone,
    isIdealistDay,
    state,
    country,
    source,
    orgID,
    logo,
    orgName,
    orgType,
  } = hit;

  const location = useMemo(
    () =>
      getWorkLocationString({
        i18n,
        remoteOptions: {
          remoteZone,
          remoteCountry: country,
          remoteState: state,
        },
        address: {
          city,
          country,
          stateCode: state,
        },
      }),
    [i18n, remoteZone, city, country, state],
  );

  const listingStats = useMemo(() => {
    const stats: ReactNode[] = [];

    if (published) {
      stats.push(
        <BodyMicro lineHeight={1}>
          {i18n('Posted %(postedAt)s', {
            postedAt: distanceInWordsToNow(fromUnixTime(published)),
          })}
        </BodyMicro>,
      );
    }

    return stats;
  }, [i18n, published]);

  const ownerOrg = user ? user.orgs.find((o) => o.id === orgID) : null;
  const startDateTime = startDate && startTime ? startsLocal : null;
  const endDateTime = endDate && endTime ? endsLocal : null;

  return (
    <SearchHitHolder data-qa-id="search-result" data-hit-id={objectID}>
      <SearchHitCard>
        <SearchHitInteractiveContentWrapper
          roundedTop={hideOwnerBanner || !ownerOrg}
          highlighted={Boolean(highlighted)}
          listingLinkData={{
            type: 'link-with-state',
            to: href,
            state: {
              showSearchContext: true,
            } satisfies SearchContextHeaderLocationState,
          }}
          onClick={() => trackClick?.()}
        >
          <SearchHitHoverEffect />

          <SearchHitImageHolder $hiddenOnMobile={!logo}>
            <SearchHitImage
              isImported={false}
              imageUrl={null}
              image={image}
              source={source}
              name={name}
              logo={logo ?? null}
            />
          </SearchHitImageHolder>

          <SearchEventHitInfo
            application={application ?? null}
            endDateTime={endDateTime}
            listingStats={listingStats}
            location={location}
            locationType={locationType}
            name={name}
            orgName={orgName ?? null}
            orgType={orgType}
            showLabel={showLabel}
            startDateTime={startDateTime}
            timezone={timezone}
            isIdealistDay={isIdealistDay}
          />
        </SearchHitInteractiveContentWrapper>

        {searchSavedListingsStore && (
          <SearchHitFloatingSaveButton
            hit={hit}
            searchSavedListingsStore={searchSavedListingsStore}
          />
        )}
      </SearchHitCard>
    </SearchHitHolder>
  );
}
